import api from './api';

export interface DomainInvestigationRequest {
  domain: string;
  collectors?: string[];
  company_id?: number;
}

export interface DomainInvestigationResponse {
  investigation_id: string;
  domain: string;
  status: string;
  message: string;
}

export interface CompanyInvestigationRequest {
  company_id: number;
  collectors?: string[];
}

export interface CompanyInvestigationResponse {
  investigation_ids: string[];
  company_id: number;
  company_name: string;
  assets_count: number;
  domains_investigated: string[];
  status: string;
  message: string;
}

export interface Investigation {
  investigation_id: string;
  domain: string;
  status: 'started' | 'running' | 'completed' | 'failed';
  created_at: string;
  completed_at?: string;
  collectors_used: string[];
  total_events: number;
  high_severity: number;
  medium_severity: number;
  low_severity: number;
}

export interface CollectorInfo {
  name: string;
  description: string;
  capabilities: string[];
}

export interface SecurityEvent {
  id: string;
  type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  description: string;
  source: string;
  url?: string;
  timestamp: string;
  metadata?: Record<string, any>;
}

export interface InvestigationReport {
  executive_summary: {
    target_domain: string;
    investigation_date: string;
    execution_time: string;
    total_findings: number;
    risk_level: 'low' | 'medium' | 'high' | 'critical';
    critical_issues: number;
    high_issues: number;
  };
  infrastructure_mapping: {
    subdomains_found: number;
    subdomains: string[];
  };
  security_findings: {
    exposed_buckets: number;
    code_leaks: number;
    paste_leaks: number;
    vulnerabilities: number;
    data_breaches: number;
    phishing_events: number;
  };
  detailed_findings: {
    investigation_id: string;
    target_domain: string;
    status: string;
    started_at: string;
    completed_at?: string;
    execution_time_seconds: number;
    collectors_used: string[];
    subdomains: string[];
    exposed_buckets: SecurityEvent[];
    code_leaks: SecurityEvent[];
    paste_leaks: SecurityEvent[];
    vulnerabilities: SecurityEvent[];
    breach_data: SecurityEvent[];
    phishing_events: SecurityEvent[];
    total_events: number;
    high_severity_count: number;
    critical_severity_count: number;
  };
  recommendations: string[];
}

export const domainInvestigationService = {
  // Iniciar investigação completa
  async startInvestigation(request: DomainInvestigationRequest): Promise<DomainInvestigationResponse> {
    const response = await api.post('/api/v2/domain-investigation/start', request);
    return response.data;
  },

  // Iniciar scan rápido
  async startQuickScan(request: DomainInvestigationRequest): Promise<DomainInvestigationResponse> {
    const response = await api.post('/api/v2/domain-investigation/quick-scan', request);
    return response.data;
  },

  // Obter status de uma investigação
  async getInvestigationStatus(investigationId: string): Promise<Investigation> {
    const response = await api.get(`/api/v2/domain-investigation/status/${investigationId}`);
    return response.data;
  },

  // Listar todas as investigações
  async listInvestigations(): Promise<{ investigations: Investigation[]; total: number }> {
    const response = await api.get('/api/v2/domain-investigation/list');
    return response.data;
  },

  // Obter relatório de investigação
  async getInvestigationReport(investigationId: string): Promise<any> {
    const response = await api.get(`/api/v2/domain-investigation/report/${investigationId}`);
    return response.data;
  },

  // Deletar investigação
  async deleteInvestigation(investigationId: string): Promise<{ message: string }> {
    const response = await api.delete(`/api/v2/domain-investigation/${investigationId}`);
    return response.data;
  },

  // Obter coletores disponíveis
  async getAvailableCollectors(): Promise<{ collectors: Record<string, CollectorInfo>; total: number }> {
    const response = await api.get('/api/v2/domain-investigation/collectors');
    return response.data;
  },

  // Iniciar investigação completa baseada em empresa
  async startCompanyInvestigation(request: CompanyInvestigationRequest): Promise<CompanyInvestigationResponse> {
    const response = await api.post('/api/v2/domain-investigation/company-scan', request);
    return response.data;
  },

  // Iniciar scan rápido baseado em empresa
  async startCompanyQuickScan(request: CompanyInvestigationRequest): Promise<CompanyInvestigationResponse> {
    const response = await api.post('/api/v2/domain-investigation/company-quick-scan', request);
    return response.data;
  },
};
